import { FC, useEffect, useState } from 'react';
import { TextField, Button, Grid, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../../components/forms/Pagination';
import { Section } from '../../../../components/ui/Section';
import SystemsList from './SystemsList';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { SystemResponse } from '../../../../app/services/api/generated';

type Props = {
  onCreateNew: () => void;
};

const SystemsTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('admin');
  const { getSystems } = useNswagClient();
  const [list, setList] = useState<SystemResponse[]>([]);
  const [filteredList, setFilteredList] = useState<SystemResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const pageSize = 20;

  const fetchSystems = async () => {
    setIsLoading(true);
    try {
      const response = await getSystems();
      if (response?.data?.systems) {
        setList(response.data.systems);
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
      setList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
  }, []);

  useEffect(() => {
    let filtered = list;
    if (searchTerm) {
      filtered = filtered.filter(s =>
        (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.description || '').toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    setTotalCount(filtered.length);
    const start = currentPage * pageSize;
    setFilteredList(filtered.slice(start, start + pageSize));
  }, [list, searchTerm, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(0);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('systems.search')}
          placeholder={t('systems.searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('systems.clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('systems.search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <SystemsList
            list={filteredList}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default SystemsTab;
